import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { SectionHero } from '../components/sections/SectionHero';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { VideoPopup } from '../components/HomepagePopup';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// NUTS Logo URL - Transparent PNG
const NUTS_LOGO = 'https://customer-assets.emergentagent.com/job_c71ccf0b-f871-4091-bddc-a51aa7862b88/artifacts/h6y7dxee_LOGO%20NUTS%20ANDORRA%20TRANSPARENT.png';

const AcademiaPage = () => {
  const { t, language } = useLanguage();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  const nutsTranslations = {
    ca: {
      title: "Vols descobrir el golf?",
      subtitle: "Et presentem el nostre Programa d'Iniciació al Golf",
      button: "Dona el primer cop!"
    },
    es: {
      title: "¿Quieres descubrir el golf?",
      subtitle: "Te presentamos nuestro Programa de Iniciación al Golf",
      button: "¡Da el primer golpe!"
    },
    fr: {
      title: "Vous voulez découvrir le golf?",
      subtitle: "Nous vous présentons notre Programme d'Initiation au Golf",
      button: "Faites le premier coup!"
    },
    en: {
      title: "Want to discover golf?",
      subtitle: "We present our Golf Initiation Program",
      button: "Take your first swing!"
    }
  };

  const nuts = nutsTranslations[language] || nutsTranslations.ca;

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const cacheBuster = Date.now();
        let response = await fetch(`${API_URL}/api/section-content/iniciacio/${language}?_t=${cacheBuster}`);
        if (response.ok) {
          const data = await response.json();
          setContent(data);
        } else {
          response = await fetch(`${API_URL}/api/content/iniciacio/${language}?_t=${cacheBuster}`);
          if (response.ok) {
            const data = await response.json();
            setContent(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch iniciacio content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [language]);

  const heroImage = content?.hero_image || 'https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/jdagpogz_ARAVELL_HERO_INICIACIO_1920x700.jpeg';
  
  let intro = '';
  if (content?.content_blocks && content.content_blocks.length > 0) {
    intro = content.content_blocks.map(block => {
      if (block.block_type === 'text') {
        const title = block.content?.title || '';
        const body = block.content?.body || '';
        return `${title ? `<h3>${title}</h3>` : ''}${body ? `<p>${body}</p>` : ''}`;
      } else if (block.block_type === 'schedule' && block.content?.items) {
        const items = block.content.items.map(item => 
          `<li><strong>${item.day}:</strong> ${item.hours}${item.price ? ` - ${item.price}` : ''}</li>`
        ).join('');
        return `<h3>Horaris</h3><ul>${items}</ul>`;
      } else if (block.block_type === 'contact' && block.content) {
        const { phone, email, address } = block.content;
        let contactHtml = '<h3>Contacte</h3><p>';
        if (phone) contactHtml += `Tel: ${phone}<br/>`;
        if (email) contactHtml += `Email: ${email}<br/>`;
        if (address) contactHtml += `${address}`;
        contactHtml += '</p>';
        return contactHtml;
      }
      return '';
    }).join('');
  } else {
    intro = content?.intro || '<p>Contingut pròximament disponible.</p>';
  }
  
  const ctaBlock = content?.content_blocks?.find(b => b.block_type === 'cta');
  const ctaText = ctaBlock?.content?.text || content?.cta_text || '';
  const ctaLink = ctaBlock?.content?.link || content?.cta_link || '/contacte';
  const title = content?.title || 'Acadèmia';
  const gallery = content?.gallery || [];

  return (
    <main data-testid="academia-page">
      <SEO title={PAGE_SEO.iniciacio.title} description={PAGE_SEO.iniciacio.description} />
      <SectionHero
        image={heroImage}
        title="Acadèmia"
        subtitle=""
      />

      {/* NUTS Program Section */}
      <section className="py-12 bg-[#F9F9F7]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-display text-2xl md:text-3xl text-[#1A4D2E] font-medium mb-4">
              {nuts.title}
            </h2>
            
            <p className="text-[#666666] text-base md:text-lg max-w-xl mx-auto mb-6">
              {nuts.subtitle}
            </p>
            
            <Link 
              to="/programa-nuts"
              className="inline-block hover:opacity-80 transition-opacity"
              data-testid="nuts-logo-link"
            >
              <motion.img
                src={NUTS_LOGO}
                alt="NUTS - Programa d'Iniciació al Golf"
                className="h-24 md:h-32 mx-auto"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />
            </Link>
            
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowVideo(true)}
                className="inline-block bg-[#1A4D2E] text-white px-8 py-3 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors"
                data-testid="nuts-cta-btn"
              >
                {nuts.button}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {showVideo && <VideoPopup onClose={() => setShowVideo(false)} />}

      {/* Main Content Section */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <div 
                className="rich-text text-[#666666] max-w-3xl mx-auto"
                dangerouslySetInnerHTML={{ __html: intro }}
              />

              {/* Image Gallery */}
              {gallery && gallery.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="mt-12"
                >
                  <div className={`grid gap-6 ${
                    gallery.length === 1 ? 'grid-cols-1 max-w-2xl mx-auto' :
                    gallery.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                    'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                  }`}>
                    {gallery.map((img, index) => (
                      <motion.div
                        key={img.id || index}
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                        onClick={() => setSelectedImage({ url: img.url, caption: img.caption || `${title} - Imatge ${index + 1}` })}
                      >
                        <div className="aspect-[4/3] bg-[#F0F2EF]">
                          <img
                            src={img.url}
                            alt={img.caption || `${title} - Imatge ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        {img.caption && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                            <p className="text-white text-sm">{img.caption}</p>
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors duration-300">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {ctaText && ctaLink && (
                <div className="mt-12 text-center">
                  <a
                    href={ctaLink}
                    target={ctaLink.startsWith('http') ? '_blank' : undefined}
                    rel={ctaLink.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="inline-block bg-[#1A4D2E] text-white px-8 py-4 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200"
                    data-testid="iniciacio-cta-btn"
                  >
                    {ctaText}
                  </a>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Gallery Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 cursor-pointer"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
            aria-label="Tancar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-full max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            {selectedImage.caption && (
              <p className="mt-4 text-white text-center text-lg">{selectedImage.caption}</p>
            )}
          </div>
        </div>
      )}
    </main>
  );
};

export default AcademiaPage;
